import React from 'react';
import { Card } from '../../../components/ui/Card';
import { Divider } from '../../../components/ui/Divider';
import { Callout } from '../../../components/ui/Callout';
import { Icon } from '../../../components/ui/Icon';
import { Badge } from '../../../components/ui/Badge';
import ParcelForm from '../../../components/calculator/ParcelForm';
import DocumentForm from '../../../components/calculator/components/DocumentForm';
import { calculateTotalWeight } from '../../../components/calculator/utils/calculationUtils';
import { PackageTypeSelector } from './PackageTypeSelector';
import { ShipStepNavigation } from './ShipStepNavigation';
import { useShippingWizard, ShippingSteps } from '../hooks/useShippingWizard';
import { useShippingHandlers } from '../hooks/useShippingHandlers';
import { ShippingType } from '../../../components/calculator/components';

interface Props {
  wizard: ReturnType<typeof useShippingWizard>;
  handlers: ReturnType<typeof useShippingHandlers>;
}

export const ShipStepPackage: React.FC<Props> = ({ wizard: w, handlers: h }) => {
  const { parcels, documents, parcelDetails, parcelErrors, unitSystem, stepValidation } = w;

  return (
    <div>
      <Card pad style={{ padding: 28 }}>
        <h2 style={{ fontSize: 20, marginBottom: 20 }}>Package Details</h2>

        <PackageTypeSelector
          selectedType={parcelDetails.packageType as ShippingType}
          onChange={h.handleShippingTypeChange}
        />

        <Divider style={{ margin: '20px 0' }} />

        {parcelDetails.packageType === 'package' ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>Parcel Information</span>
              <Badge tone="default">{parcels.length} parcel{parcels.length !== 1 ? 's' : ''}</Badge>
            </div>

            {parcels.map((parcel, index) => (
              <div key={parcel.id} style={{ marginBottom: 16 }}>
                {parcelErrors[parcel.id] && Object.keys(parcelErrors[parcel.id]).length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Callout tone="warn">
                      <Icon name="alertCircle" size={14} />
                      <span style={{ fontSize: 13 }}>
                        Parcel {index + 1}:{' '}
                        {parcelErrors[parcel.id].weightLimit
                          ? 'Exceeds weight limit for selected FedEx packaging'
                          : 'Has missing or invalid fields'}
                      </span>
                    </Callout>
                  </div>
                )}
                <ParcelForm
                  showPrice={false}
                  key={parcel.id}
                  id={parcel.id}
                  quantity={parcel.quantity}
                  weight={parcel.weight}
                  length={parcel.length}
                  width={parcel.width}
                  height={parcel.height}
                  physicalWeight={parcel.physicalWeight}
                  dimensionalWeight={parcel.dimensionalWeight}
                  unitSystem={unitSystem}
                  fedexPackagingType={parcel.fedexPackagingType}
                  onUpdate={h.handleUpdateParcel}
                  onRemove={h.handleRemoveParcel}
                />
              </div>
            ))}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>Document Information</span>
              <Badge tone="default">{documents.length} document{documents.length !== 1 ? 's' : ''}</Badge>
            </div>

            {documents.map((document, index) => (
              <DocumentForm
                key={document.id}
                document={document}
                index={index}
                unitSystem={unitSystem}
                onUpdateDocument={h.handleUpdateDocument}
                onRemoveDocument={h.handleRemoveDocument}
                canRemove={documents.length > 1}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={parcelDetails.packageType === 'package' ? h.handleAddParcel : h.handleAddDocument}
          style={{
            width: '100%',
            marginTop: 12,
            padding: '12px 16px',
            borderRadius: 8,
            border: '2px dashed var(--blue)',
            background: 'transparent',
            color: 'var(--blue)',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Icon name="plus" size={16} />
          Add Another {parcelDetails.packageType === 'document' ? 'Document' : 'Parcel'}
        </button>

        {((parcelDetails.packageType === 'package' && parcels.length > 0) ||
          (parcelDetails.packageType === 'document' && documents.length > 0)) && (
          <div
            style={{
              marginTop: 20,
              padding: 16,
              borderRadius: 8,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Weight Summary</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
              {parcelDetails.packageType === 'package'
                ? parcels.map((parcel, index) => (
                    <div
                      key={parcel.id}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--surface)',
                        borderRadius: 6,
                        textAlign: 'center',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div className="muted" style={{ fontSize: 11 }}>Parcel {index + 1}</div>
                      <div className="mono" style={{ fontWeight: 600, fontSize: 16 }}>
                        {Math.max(parcel.physicalWeight || 0, parcel.dimensionalWeight || 0).toFixed(2)}
                      </div>
                      <div className="muted" style={{ fontSize: 11 }}>{unitSystem === 'metric' ? 'kg' : 'lb'}</div>
                    </div>
                  ))
                : documents.map((document, index) => (
                    <div
                      key={document.id}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--surface)',
                        borderRadius: 6,
                        textAlign: 'center',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div className="muted" style={{ fontSize: 11 }}>Doc {index + 1}</div>
                      <div className="mono" style={{ fontWeight: 600, fontSize: 16 }}>
                        {(document.weight * document.quantity).toFixed(2)}
                      </div>
                      <div className="muted" style={{ fontSize: 11 }}>{unitSystem === 'metric' ? 'kg' : 'lb'}</div>
                    </div>
                  ))}
            </div>
            {parcelDetails.packageType === 'package' && (
              <div style={{ marginTop: 10, fontSize: 13 }}>
                <span className="muted">Total: </span>
                <span className="mono" style={{ fontWeight: 600 }}>
                  {calculateTotalWeight(parcels).toFixed(2)} {unitSystem === 'metric' ? 'kg' : 'lb'}
                </span>
              </div>
            )}
          </div>
        )}
      </Card>

      <ShipStepNavigation
        onBack={w.handleBackToSearch}
        backLabel="Back to Calculator"
        onNext={w.goToNextStep}
        nextLabel="Continue to Service Selection"
        nextDisabled={!stepValidation[ShippingSteps.PACKAGE_DETAILS]}
        validationMessage={
          !stepValidation[ShippingSteps.PACKAGE_DETAILS]
            ? 'Please complete all required fields'
            : undefined
        }
      />
    </div>
  );
};
